import { useState, useCallback, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider } from './context/AuthContext';
import { DataProvider, useData } from './context/DataContext';
import ScrollToTop from './components/shared/ScrollToTop';
import Splash from './components/startup/Splash';
import Loader from './components/startup/Loader';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import Home from './pages/Home';
import Products from './pages/Products';
import About from './pages/About';
import Contact from './pages/Contact';
import Services from './pages/Services';
import Careers from './pages/Careers';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Login from './pages/Login';
import Register from './pages/Register';
import DashboardLayout from './pages/dashboard/DashboardLayout';
import DashboardOverview from './pages/dashboard/DashboardOverview';
import ContentPage from './pages/dashboard/ContentPage';
import ContentOverview from './pages/dashboard/ContentOverview';
import CompanyPage from './pages/dashboard/CompanyPage';
import UsersPage from './pages/dashboard/UsersPage';
import MessagesPage from './pages/dashboard/MessagesPage';
import NewsletterPage from './pages/dashboard/NewsletterPage';
import ProfilePage from './pages/dashboard/ProfilePage';
import './App.css';

function PublicLayout() {
  const { data } = useData();
  const navItems = data?.navigation || [];

  return (
    <div className="app">
      <Header navItems={navItems} />
      <div className="app__content">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/products" element={<Products />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/services" element={<Services />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
        </Routes>
      </div>
      <Footer />
    </div>
  );
}

function AppContent() {
  const [phase, setPhase] = useState('splash');

  useEffect(() => {
    const visited = sessionStorage.getItem('iwx-visited');
    if (visited) {
      setPhase('ready');
    }
  }, []);

  const handleSplashComplete = useCallback(() => {
    setPhase('loading');
  }, []);

  const handleLoadingComplete = useCallback(() => {
    sessionStorage.setItem('iwx-visited', 'true');
    setPhase('ready');
  }, []);

  if (phase === 'splash') {
    return <Splash onComplete={handleSplashComplete} />;
  }

  if (phase === 'loading') {
    return <Loader onComplete={handleLoadingComplete} />;
  }

  return (
    <Routes>
      {/* Dashboard — own layout, no header/footer */}
      <Route path="/dashboard" element={<DashboardLayout />}>
        <Route index element={<DashboardOverview />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="messages" element={<MessagesPage />} />
        <Route path="newsletter" element={<NewsletterPage />} />
        <Route path="company" element={<CompanyPage />} />
        <Route path=":section/:id" element={<ContentOverview />} />
        <Route path=":section" element={<ContentPage />} />
      </Route>

      {/* Public pages — with header/footer */}
      <Route path="/*" element={<PublicLayout />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <DataProvider>
          <Router>
            <ScrollToTop />
            <AppContent />
          </Router>
        </DataProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

